// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The legend of a DSA 1.8 vector layer (notes §8), built from its style:
 * the colour scale (a bar with its domain) or the category / step list,
 * and for points a size key drawn at the `legend.size.values` (M3, M5, M7
 * circles for quakes). The DSA's `legend` block gives the titles.
 *
 * `vectorLegendOf` is the description, shared by the browser's floating
 * legend (`renderVectorLegend`, in the colour bar's box) and the
 * headset's (vrLegend). When the vector layer supersedes the dataset's
 * video or its legend picture, this legend replaces the dataset's;
 * otherwise its keys go under the dataset's own bar (main.ts).
 */
import { kmPerDegOf, type CompiledVectorLayer } from './vectorModel'
import { evalColor, evalNumber, localize, paletteAt, parseHex, shownUnits, type Rgba } from './vectorStyle'
import { displayUnitOf, replaceUnits } from './vectorUnits'
import type { VectorField } from './vectorTypes'

export interface VectorLegendModel {
  title: string
  color:
    /** `stops`: CSS gradient stops with their positions; `samples`: the same ramp as 24 even colours, low to high. */
    | { kind: 'bar'; label: string; stops: string[]; samples: string[]; ticks: Array<{ position: number; label: string }>; units: string }
    | { kind: 'classes'; label: string; classes: Array<{ color: string; label: string }> }
    | null
  /** Points only: the key circles, their diameters relative to the largest (1). */
  size: { label: string; items: Array<{ label: string; relative: number }> } | null
}

function cssOf(c: Rgba): string {
  return `rgba(${Math.round(c[0] * 255)}, ${Math.round(c[1] * 255)}, ${Math.round(c[2] * 255)}, ${+c[3].toFixed(3)})`
}

/** A key's number: whole numbers without decimals (M3, 700), else the field's precision. */
function fmt(value: number, precision: number | undefined, lang: string): string {
  const p = Number.isInteger(value) ? 0 : precision ?? (Math.abs(value) >= 100 ? 0 : 1)
  return value.toLocaleString(lang, { minimumFractionDigits: p, maximumFractionDigits: p })
}

/** A key's number in the units the viewer reads (speeds in km/h, vectorUnits.ts). */
function fmtField(value: number, field: VectorField | undefined, lang: string): string {
  const unit = displayUnitOf(field)
  return unit ? fmt(value * unit.factor, unit.precision, lang) : fmt(value, field?.precision, lang)
}

/** The layer's legend, or null when it has no `legend` block and nothing to key. */
export function vectorLegendOf(layer: CompiledVectorLayer, lang: string): VectorLegendModel | null {
  const spec = layer.spec
  const legend = spec.legend
  if (!legend) return null
  const style = spec.style
  const fields = spec.fields
  const color = style.color
  let colorKey: VectorLegendModel['color'] = null
  const colorField = color && 'field' in color ? fields.find((f) => f.name === color.field) : undefined
  const colorLabel = replaceUnits(localize(legend.color?.label, lang), colorField)
  if (color && 'field' in color) {
    const field = colorField
    if (color.map === 'category') {
      colorKey = {
        kind: 'classes', label: colorLabel,
        classes: (field?.categories ?? []).map((c) => {
          const rgba = evalColor(color, { [color.field]: c.value }, fields, [0.5, 0.5, 0.5, 1]) ?? parseHex(c.color) ?? [0.5, 0.5, 0.5, 1]
          return { color: cssOf(rgba), label: localize(c.label, lang) || String(c.value) }
        }),
      }
    } else if (color.map === 'step' && 'colors' in color) {
      const d = color.domain
      const classes = color.colors.map((hex, i) => {
        const label = i === 0 ? `< ${fmtField(d[0], field, lang)}`
          : i >= d.length ? `≥ ${fmtField(d[d.length - 1], field, lang)}`
          : `${fmtField(d[i - 1], field, lang)}–${fmtField(d[i], field, lang)}`
        return { color: cssOf(parseHex(hex) ?? [0.5, 0.5, 0.5, 1]), label }
      })
      colorKey = { kind: 'classes', label: colorLabel, classes }
    } else if ('palette' in color) {
      const [lo, hi] = color.domain
      const stops = color.palette.stops.map((s) => `${cssOf(paletteAt(color.palette.stops, s.t))} ${(s.t * 100).toFixed(1)}%`)
      const ticks = [0, 0.5, 1].map((p) => {
        const v = color.map === 'log' && lo > 0 && hi > 0 ? lo * Math.pow(hi / lo, p) : lo + (hi - lo) * p
        return { position: p, label: fmtField(v, field, lang) }
      })
      const samples = Array.from({ length: 24 }, (_, i) => cssOf(paletteAt(color.palette.stops, i / 23)))
      colorKey = { kind: 'bar', label: colorLabel, stops, samples, ticks, units: shownUnits(field) }
    }
  }
  let sizeKey: VectorLegendModel['size'] = null
  const size = style.size
  const sizeField = size && typeof size.value === 'object' ? size.value.field : null
  if (spec.geometry === 'point' && legend.size?.values?.length && size && sizeField) {
    const field = fields.find((f) => f.name === sizeField)
    const diameters = legend.size.values.map((v) => {
      const d = evalNumber(size.value, { [sizeField]: v }, 0) ?? 0
      return size.units === 'km' ? d / kmPerDegOf(layer) : d
    })
    const largest = Math.max(...diameters, 1e-9)
    sizeKey = {
      label: replaceUnits(localize(legend.size.label, lang), field),
      items: legend.size.values.map((v, i) => ({ label: displayUnitOf(field) ? fmtField(v, field, lang) : fmt(v, field?.precision ?? 0, lang), relative: diameters[i] / largest })),
    }
  }
  if (!colorKey && !sizeKey) return null
  return { title: localize(legend.title, lang) || layer.name, color: colorKey, size: sizeKey }
}

/** The colour of a constant-coloured layer, for a one-swatch key. */
export function constantColorOf(layer: CompiledVectorLayer): string | null {
  const c = layer.spec.style.color
  return c && 'constant' in c ? cssOf(parseHex(c.constant) ?? [1, 1, 1, 1]) : null
}

/** The circle key as DOM, for the browser legend. */
function sizeKeyElement(size: NonNullable<VectorLegendModel['size']>, maxPx: number): HTMLElement {
  const row = document.createElement('div')
  row.className = 'vector-legend-sizes'
  if (size.label) {
    const label = document.createElement('div')
    label.className = 'vector-legend-label'
    label.textContent = size.label
    row.appendChild(label)
  }
  const list = document.createElement('div')
  list.className = 'vector-legend-size-list'
  for (const item of size.items) {
    const cell = document.createElement('div')
    cell.className = 'vector-legend-size'
    const dot = document.createElement('span')
    dot.className = 'vector-legend-dot'
    const px = Math.max(3, Math.round(maxPx * item.relative))
    dot.style.inlineSize = `${px}px`
    dot.style.blockSize = `${px}px`
    const text = document.createElement('span')
    text.textContent = item.label
    cell.append(dot, text)
    list.appendChild(cell)
  }
  row.appendChild(list)
  return row
}

/** One model's colour key and size key, as sections of a legend. */
export function vectorLegendSections(model: VectorLegendModel, withTitle: boolean): HTMLElement[] {
  const out: HTMLElement[] = []
  if (withTitle) {
    const title = document.createElement('div')
    title.className = 'vector-legend-title'
    title.textContent = model.title
    out.push(title)
  }
  const c = model.color
  if (c?.kind === 'bar') {
    if (c.label) {
      const label = document.createElement('div')
      label.className = 'vector-legend-label'
      label.textContent = c.label
      out.push(label)
    }
    const gradient = document.createElement('div')
    gradient.className = 'panel-colorbar-gradient'
    gradient.style.setProperty('--colorbar-ramp', `linear-gradient(to right, ${c.stops.join(', ')})`)
    const axis = document.createElement('div')
    axis.className = 'panel-colorbar-axis'
    for (const tick of c.ticks) {
      const mark = document.createElement('span')
      mark.className = tick.position === 0 ? 'panel-colorbar-tick panel-colorbar-end panel-colorbar-end-low'
        : tick.position === 1 ? 'panel-colorbar-tick panel-colorbar-end panel-colorbar-end-high' : 'panel-colorbar-tick'
      if (tick.position > 0 && tick.position < 1) mark.style.insetInlineStart = `${(tick.position * 100).toFixed(2)}%`
      mark.textContent = tick.label
      axis.appendChild(mark)
    }
    out.push(gradient, axis)
  } else if (c?.kind === 'classes') {
    if (c.label) {
      const label = document.createElement('div')
      label.className = 'vector-legend-label'
      label.textContent = c.label
      out.push(label)
    }
    const list = document.createElement('ul')
    list.className = 'panel-class-legend-list'
    for (const cl of c.classes) {
      const item = document.createElement('li')
      item.className = 'panel-class-legend-item'
      const swatch = document.createElement('span')
      swatch.className = 'panel-class-legend-swatch'
      swatch.setAttribute('aria-hidden', 'true')
      swatch.style.backgroundColor = cl.color
      const label = document.createElement('span')
      label.className = 'panel-class-legend-label'
      label.textContent = cl.label
      item.append(swatch, label)
      list.appendChild(item)
    }
    out.push(list)
  }
  if (model.size) out.push(sizeKeyElement(model.size, 22))
  return out
}

/**
 * The floating legend of vector layers that replace the dataset's: in the
 * colour bar's box (`.panel-colorbar`), so it sits, lifts and moves as
 * the bar does. `ariaLabel` names it for screen readers.
 */
export function renderVectorLegend(models: VectorLegendModel[], ariaLabel: string): HTMLElement | null {
  if (!models.length) return null
  const el = document.createElement('div')
  el.className = 'panel-colorbar vector-legend'
  el.setAttribute('role', 'group')
  el.setAttribute('aria-label', ariaLabel)
  const column = document.createElement('div')
  column.className = 'panel-colorbar-scale'
  for (const m of models) column.append(...vectorLegendSections(m, true))
  el.appendChild(column)
  return el
}

/**
 * The legend as one wide picture for a Studio render's compositor, which
 * draws a legend 70 px high (at 1080p) and up to 30 % of the frame wide:
 * on one row, the title and colour label, the bar with its ends and middle
 * (or the class swatches), and the size circles. 8 : 1, drawn at twice the
 * size it is shown.
 */
export function vectorLegendCanvas(model: VectorLegendModel, makeCanvas: (w: number, h: number) => HTMLCanvasElement = (w, h) => Object.assign(document.createElement('canvas'), { width: w, height: h })): HTMLCanvasElement | null {
  const W = 1120, H = 140
  const canvas = makeCanvas(W, H)
  const g = canvas.getContext('2d')
  if (!g) return null
  const font = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
  g.fillStyle = 'rgba(20, 24, 32, 0.78)'
  g.beginPath()
  g.roundRect(0, 0, W, H, 20)
  g.fill()
  g.textBaseline = 'middle'
  const text = (s: string, x: number, y: number, size: number, weight = 500, colour = '#f2f5f9', align: CanvasTextAlign = 'left', max?: number) => {
    g.font = `${weight} ${size}px ${font}`
    g.fillStyle = colour
    g.textAlign = align
    g.fillText(s, x, y, max)
  }
  // The title and what the colour means, on the left.
  text(model.title, 28, 46, 30, 700, '#f2f5f9', 'left', 280)
  if (model.color?.label) text(model.color.label, 28, 94, 22, 500, 'rgba(232, 234, 240, 0.75)', 'left', 280)
  const x0 = 330
  const x1 = model.size ? 820 : W - 30
  const c = model.color
  if (c?.kind === 'bar') {
    const grad = g.createLinearGradient(x0, 0, x1, 0)
    c.samples.forEach((s, i) => grad.addColorStop(i / Math.max(1, c.samples.length - 1), s))
    g.fillStyle = grad
    g.beginPath()
    g.roundRect(x0, 34, x1 - x0, 30, 6)
    g.fill()
    for (const tick of c.ticks) {
      const x = x0 + tick.position * (x1 - x0)
      text(tick.label, x, 94, 22, 600, '#f2f5f9', tick.position === 0 ? 'left' : tick.position === 1 ? 'right' : 'center')
    }
  } else if (c?.kind === 'classes') {
    const n = Math.max(1, c.classes.length)
    const cell = (x1 - x0) / n
    c.classes.forEach((cl, i) => {
      g.fillStyle = cl.color
      g.fillRect(x0 + i * cell + 4, 34, cell - 8, 30)
      text(cl.label, x0 + i * cell + cell / 2, 94, 20, 600, '#f2f5f9', 'center', cell - 6)
    })
  }
  if (model.size) {
    const s = model.size
    const cell = (W - 30 - 850) / s.items.length
    const maxD = Math.min(70, cell - 10)
    s.items.forEach((item, i) => {
      const cx = 850 + cell * (i + 0.5)
      const d = Math.max(8, maxD * item.relative)
      g.beginPath()
      g.arc(cx, 66 - d / 2 + maxD / 2 - 6, d / 2, 0, Math.PI * 2)
      g.fillStyle = 'rgba(255, 255, 255, 0.16)'
      g.fill()
      g.strokeStyle = '#f2f5f9'
      g.lineWidth = 2
      g.stroke()
      text(item.label, cx, 118, 22, 600, '#f2f5f9', 'center')
    })
    if (s.label) text(s.label, 850 + (W - 30 - 850) / 2, 18, 18, 500, 'rgba(232, 234, 240, 0.75)', 'center')
  }
  return canvas
}
